import React, { useState } from 'react'

import cn from 'classnames'

import ChainToken from '@src/components/ChainToken'
import CopyToClipboard from '@src/components/CopyToClipboard'
import ChevronDownIcon from '@src/components/icons/ChevronDown'
import CloseIcon from '@src/components/icons/Close'
import ProcessFailedIcon from '@src/components/icons/ProcessFailed'
import UpArrowCorner from '@src/components/icons/UpArrowCorner'
import { StepNames } from '@src/enums'
import { IExchangeData } from '@src/types/exchange'
import { IFee } from '@src/types/fee'
import { formatExchangeAmountWithCommas } from '@src/utils/exchangeUtils'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
import { truncateAddress } from '@src/utils/truncateAddress'
import { useConfigStore } from '@src/zustand_stores'

interface IFeatherSwapFailedProps {
    sellData: IExchangeData
    buyData: IExchangeData
    fees: IFee[]
    orderId: string
    currentStep: StepNames
    result?: {
        sourceTxHash?: string
        destinationTxHash?: string
        message?: string
    }
}

const FeatherSwapFailed: React.FC<IFeatherSwapFailedProps> = ({
    sellData,
    buyData,
    fees,
    orderId,
    currentStep,
    result = undefined,
}) => {
    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const [showDetails, setShowDetails] = useState(true)

    // Get transaction hashes
    const sourceTxHash = result?.sourceTxHash?.includes('/')
        ? result.sourceTxHash.substring(result.sourceTxHash.lastIndexOf('/') + 1)
        : result?.sourceTxHash

    const destinationTxHash = result?.destinationTxHash?.includes('/')
        ? result.destinationTxHash.substring(result.destinationTxHash.lastIndexOf('/') + 1)
        : result?.destinationTxHash

    // Format the transaction hashes for display
    const formattedSourceTxHash = sourceTxHash ? truncateAddress(sourceTxHash) : ''
    const formattedDestinationTxHash = destinationTxHash ? truncateAddress(destinationTxHash) : ''

    // Build explorer URLs if not already full URLs
    const sourceTxExplorerUrl = result?.sourceTxHash?.startsWith('http')
        ? result.sourceTxHash
        : sourceTxHash
          ? `${getBlockExplorerUrlFromChainId(sellData.chainId.toString())}/tx/${sourceTxHash}`
          : ''

    const destinationTxExplorerUrl = result?.destinationTxHash?.startsWith('http')
        ? result.destinationTxHash
        : destinationTxHash
          ? `${getBlockExplorerUrlFromChainId(buyData.chainId.toString())}/tx/${destinationTxHash}`
          : ''

    // Determine failure message based on step
    const getFailureMessage = () => {
        if (result?.message) {
            return result.message
        }

        if (currentStep === StepNames.FEATHER_EXPIRED) {
            return 'Order expired. Please create a new order.'
        }

        return 'There was a problem processing your order. Please contact support.'
    }

    return (
        <div className='space-y-10 px-4'>
            <div className='flex w-full justify-center'>
                <p className='text-base font-medium text-[#E84142]'>Swap Failed</p>
            </div>

            {/* Token Icons with X between them */}
            <div className='relative flex h-[212px] items-center justify-between rounded-3xl border border-[#1A1B20] px-8'>
                <div className='z-10 flex h-[104px] w-[104px] items-center justify-center rounded-full bg-[#161618]'>
                    <ChainToken
                        tokenLogo={sellData.token?.logoURI}
                        networkIcon={availableNetworks[sellData.chainId]?.icon}
                        tokenIconSize={50}
                        chainIconSize={20}
                    />
                </div>
                <div className='absolute left-1/2 top-0 flex h-full -translate-x-1/2 items-center justify-between gap-2'>
                    <ProcessFailedIcon />
                    <div className='flex h-5 w-5 items-center justify-center rounded-full bg-[#16171A]'>
                        <CloseIcon className='text-[#E84142]' />
                    </div>
                    <ProcessFailedIcon />
                </div>
                <div className='z-10 flex h-[104px] w-[104px] items-center justify-center rounded-full bg-[#161618]'>
                    <ChainToken
                        tokenLogo={buyData.token?.logoURI}
                        networkIcon={availableNetworks[buyData.chainId]?.icon}
                        tokenIconSize={50}
                        chainIconSize={20}
                    />
                </div>
            </div>

            <div className='space-y-6'>
                {/* Error Message */}
                <div className='flex justify-center'>
                    <span className='text-sm text-mach-sub-text'>{getFailureMessage()}</span>
                </div>

                {/* Details Toggle */}
                <div className='flex items-center gap-4'>
                    <div className='flex-grow border border-[#313339]'></div>
                    <div
                        className='flex cursor-pointer items-center gap-1'
                        onClick={() => setShowDetails(!showDetails)}
                    >
                        <span className='text-sm text-mach-accent-forground'>Details</span>
                        <ChevronDownIcon className={cn('h-4 w-4 transition-all', !showDetails ? 'rotate-180' : '')} />
                    </div>
                    <div className='flex-grow border border-[#313339]'></div>
                </div>

                {/* Collapsible Details Section */}
                <div
                    className={cn(
                        'flex flex-col space-y-4 overflow-hidden transition-all duration-500',
                        showDetails ? 'h-auto opacity-100' : 'h-0 opacity-0'
                    )}
                >
                    {/* Order Details Card */}
                    <div className='space-y-4 rounded-xl bg-[#1A1B1E]/50 p-4'>
                        {/* Order ID */}
                        <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                            <span className='text-mach-sub-text'>Order ID</span>
                            <div className='flex items-center gap-[6px]'>
                                <CopyToClipboard anchorId='order-id' textToCopy={orderId} />
                                <span className='font-mono text-sm'>{truncateAddress(orderId)}</span>
                            </div>
                        </div>

                        {/* Source Transaction Hash */}
                        {sourceTxHash && (
                            <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                                <span className='text-mach-sub-text'>Deposit Transaction</span>
                                <div className='flex items-center gap-[6px]'>
                                    <CopyToClipboard anchorId='deposit-tx' textToCopy={sourceTxHash} />
                                    <span className='text-sm'>{formattedSourceTxHash}</span>
                                    {sourceTxExplorerUrl && (
                                        <a
                                            className='flex cursor-pointer text-mach-sub-text'
                                            href={sourceTxExplorerUrl}
                                            target='_blank'
                                            rel='noreferrer'
                                        >
                                            <UpArrowCorner className='h-3 w-3' />
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Destination Transaction Hash */}
                        {destinationTxHash && (
                            <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                                <span className='text-mach-sub-text'>Destination Transaction</span>
                                <div className='flex items-center gap-[6px]'>
                                    <CopyToClipboard anchorId='dest-tx' textToCopy={destinationTxHash} />
                                    <span className='text-sm'>{formattedDestinationTxHash}</span>
                                    {destinationTxExplorerUrl && (
                                        <a
                                            className='flex cursor-pointer text-mach-sub-text'
                                            href={destinationTxExplorerUrl}
                                            target='_blank'
                                            rel='noreferrer'
                                        >
                                            <UpArrowCorner className='h-3 w-3' />
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Exchange Type */}
                        <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                            <span className='text-mach-sub-text'>Exchange Type</span>
                            <span className='rounded-md bg-blue-500/20 px-2 py-0.5 text-xs text-blue-400'>
                                Feather Order
                            </span>
                        </div>

                        {/* From: Amount and Chain */}
                        <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                            <span className='text-mach-sub-text'>From</span>
                            <div className='flex items-center gap-2'>
                                <ChainToken
                                    tokenLogo={sellData.token?.logoURI}
                                    networkIcon={availableNetworks[sellData.chainId]?.icon}
                                    tokenIconSize={20}
                                    chainIconSize={10}
                                />
                                <span className='text-sm'>
                                    {formatExchangeAmountWithCommas(sellData)} {sellData.token?.symbol} on{' '}
                                    {availableNetworks[sellData.chainId]?.name}
                                </span>
                            </div>
                        </div>

                        {/* To: Amount and Chain */}
                        <div className='flex flex-col justify-between gap-2 md:flex-row md:items-center'>
                            <span className='text-mach-sub-text'>To</span>
                            <div className='flex items-center gap-2'>
                                <ChainToken
                                    tokenLogo={buyData.token?.logoURI}
                                    networkIcon={availableNetworks[buyData.chainId]?.icon}
                                    tokenIconSize={20}
                                    chainIconSize={10}
                                />
                                <span className='text-sm'>
                                    {formatExchangeAmountWithCommas(buyData)} {buyData.token?.symbol} on{' '}
                                    {availableNetworks[buyData.chainId]?.name}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer Buttons */}
            <div className='grid grid-cols-1 gap-3 pt-4 md:grid-cols-2'>
                <button
                    className='bg-mach-button-primary hover:bg-mach-button-hover rounded-xl px-4 py-3 font-medium text-white transition-colors'
                    onClick={() => window.location.reload()}
                >
                    Create New Swap
                </button>
                <a
                    href='https://discord.gg/mach'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='flex items-center justify-center rounded-xl bg-[#1A1B1E] px-4 py-3 font-medium text-white transition-colors hover:bg-[#25262B]'
                >
                    Contact Support
                </a>
            </div>
        </div>
    )
}

export default FeatherSwapFailed
